import { Injectable, inject } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { FeatureFlagService } from '@next/core-lib/featureflag';
import { LocaleService } from '@next/core-lib/i18n';
import { ParentNavigationItem } from '@next/core-lib/navigation';
import { AccessRule, SelectedCustomer, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { filter, forkJoin, map, Observable, of, switchMap, take } from 'rxjs';

import { catchError } from 'rxjs/operators';

import { Environment } from 'src/environments/environment.model';

import { AgroVisionUrlService } from './agro-vision-url.service';
import { getStaticUserNavigationItems } from '../data/all-user-navigation-items';
import { UserNavigationItem, UserNavigationLeafItem } from '../models/shared.models';

@Injectable({
  providedIn: 'root',
})
export class NavigationItemsService {
  private readonly env = inject<Environment>(EnvironmentInjector);
  private readonly agroVisionApplications: ReadonlyArray<{
    readonly applicationType: 'cultivation' | 'goat' | 'dairyCattle';
    readonly applicationKey: string;
    readonly iconKey: string;
    readonly label: string;
  }> = [
    {
      applicationType: 'goat',
      applicationKey: 'AgrifirmFocusGoat',
      iconKey: 'goat',
      label: 'app.management.agrifirm-focus-goat',
    },
    {
      applicationType: 'cultivation',
      applicationKey: 'AgrifirmFocusCultivation',
      iconKey: 'twig',
      label: 'app.management.agrifirm-focus-arable',
    },
    {
      applicationType: 'dairyCattle',
      applicationKey: 'AgrifirmFocusDairyCattleWeb',
      iconKey: 'cow',
      label: 'app.management.agrifirm-focus-dairy-cattle',
    },
  ];

  constructor(
    private readonly localeService: LocaleService,
    private readonly store: Store,
    private readonly featureFlagService: FeatureFlagService,
    private readonly agroVisionUrlService: AgroVisionUrlService,
  ) {}

  getAgroVisionNavigationItems(
    customerId: number,
    authorizedUserApplications: AccessRule[],
  ): Observable<UserNavigationItem[]> {
    const authorizedApplicationKeys = new Set(
      authorizedUserApplications.map((application) => application.ApplicationKey),
    );
    const navigationRequests = this.agroVisionApplications
      .filter((application) => authorizedApplicationKeys.has(application.applicationKey))
      .map((application) =>
        this.agroVisionUrlService.getAgroVisionUrl(application.applicationType, customerId).pipe(
          map((url) => ({ ...application, url })),
          catchError(() => of({ ...application, url: null })),
        ),
      );

    if (!navigationRequests.length) {
      return of([]);
    }

    return forkJoin(navigationRequests).pipe(
      map((items) =>
        items
          .filter((item) => item.url)
          .map((item) =>
            this.createNavigationItem(
              item.url.toString(),
              item.iconKey,
              item.label,
              item.applicationKey,
            ),
          ),
      ),
    );
  }

  getMatchingNavigationItems(
    authorizedUserApplications: AccessRule[],
    selectedLocale: string,
  ): Observable<ParentNavigationItem[]> {
    if (!authorizedUserApplications?.length) {
      return of([]);
    }

    return this.getSelectedCustomer().pipe(
      switchMap((customer) =>
        forkJoin({
          featureFlags: this.getFeatureFlags(customer),
          agroVisionItems: this.getAgroVisionNavigationItems(
            customer.id,
            authorizedUserApplications,
          ),
        }).pipe(
          map(({ featureFlags, agroVisionItems }) => [
            ...getStaticUserNavigationItems(this.env, featureFlags, customer),
            ...agroVisionItems,
          ]),
          map((items) =>
            this.getTranslatedNavigationItems(items, authorizedUserApplications, selectedLocale),
          ),
        ),
      ),
    );
  }

  private getSelectedCustomer(): Observable<SelectedCustomer> {
    return this.store.select(UserSelectors.selectCustomer).pipe(
      filter((customer): customer is SelectedCustomer => customer?.id != null),
      take(1),
    );
  }

  private getTranslatedNavigationItems(
    userNavigationItems: UserNavigationItem[],
    authorizedUserApplications: AccessRule[],
    selectedLocale: string,
  ): ParentNavigationItem[] {
    return userNavigationItems
      .filter((userNavigationItem) =>
        this.isMatchingUserNavigationItem(
          authorizedUserApplications,
          userNavigationItem,
          selectedLocale,
        ),
      )
      .map((userNavigationItem) =>
        this.translateNavigationItem(
          userNavigationItem,
          authorizedUserApplications,
          selectedLocale,
        ),
      )
      .filter(
        (userNavigationItem) =>
          !userNavigationItem.children || userNavigationItem.children.length > 0,
      );
  }

  private translateNavigationItem(
    userNavigationItem: UserNavigationItem,
    authorizedUserApplications: AccessRule[],
    selectedLocale: string,
  ) {
    return {
      ...userNavigationItem,
      label: this.localeService.translate(userNavigationItem.label),
      applicationKey: null as string,
      children: this.getTranslatedChildNavigationItems(
        userNavigationItem.children,
        authorizedUserApplications,
        selectedLocale,
      ),
    };
  }

  private getTranslatedChildNavigationItems(
    childNavigationItems: UserNavigationLeafItem[] | undefined,
    authorizedUserApplications: AccessRule[],
    selectedLocale: string,
  ): UserNavigationLeafItem[] | undefined {
    return childNavigationItems
      ?.filter((childUserNavigationItem) =>
        this.isMatchingUserNavigationItem(
          authorizedUserApplications,
          childUserNavigationItem,
          selectedLocale,
        ),
      )
      .map((childUserNavigationItem) => ({
        ...childUserNavigationItem,
        label: this.localeService.translate(childUserNavigationItem.label),
        applicationKey: null as string,
      }));
  }

  private getFeatureFlags(customer: SelectedCustomer): Observable<string[]> {
    const salesOrganization = customer.shipTos?.[0]?.salesAreas?.[0]?.salesOrganization;

    if (!salesOrganization) {
      return of([]);
    }

    return this.featureFlagService.getFeatureFlagsForSalesArea(salesOrganization).pipe(
      take(1),
      map((featureFlags) => featureFlags ?? []),
      catchError(() => of([])),
    );
  }

  private getAccessRoleForUserNavigationItem(
    authorizedUserApplications: AccessRule[],
    userNavigationItem: UserNavigationItem,
  ): string {
    return authorizedUserApplications.find(
      (userApplication) => userApplication.ApplicationKey === userNavigationItem.applicationKey,
    )?.RoleKey;
  }

  private isMatchingUserNavigationItem(
    authorizedUserApplications: AccessRule[],
    userNavigationItem: UserNavigationItem,
    selectedLocale: string,
  ): boolean {
    const authorizedUserApplicationsKeys = authorizedUserApplications.map(
      (application: AccessRule) => application.ApplicationKey,
    );

    if (this.checkHideForRolesCondition(authorizedUserApplications, userNavigationItem)) {
      return false;
    }

    if (userNavigationItem.hideForLocales?.includes(selectedLocale)) {
      return false;
    }

    if (
      userNavigationItem.applicationKey &&
      !authorizedUserApplicationsKeys.includes(userNavigationItem.applicationKey)
    ) {
      return false;
    }

    return !(
      userNavigationItem.applicationKeys &&
      !userNavigationItem.applicationKeys.every((applicationKey: string) =>
        authorizedUserApplicationsKeys.includes(applicationKey),
      )
    );
  }

  private checkHideForRolesCondition(
    authorizedUserApplications: AccessRule[],
    userNavigationItem: UserNavigationItem,
  ): boolean {
    if (!userNavigationItem.hideForRoles) {
      return false;
    }

    if (
      userNavigationItem.applicationKey &&
      userNavigationItem.hideForRoles.includes(
        this.getAccessRoleForUserNavigationItem(authorizedUserApplications, userNavigationItem),
      )
    ) {
      return true;
    }

    return (
      userNavigationItem.applicationKeys &&
      authorizedUserApplications
        .filter((userApplication) =>
          userNavigationItem.applicationKeys.includes(userApplication.ApplicationKey),
        )
        .some((userApplication) =>
          userNavigationItem.hideForRoles.includes(userApplication.RoleKey),
        )
    );
  }

  private createNavigationItem(
    absoluteUrl: string,
    iconKey: string,
    label: string,
    applicationKey: string,
  ): UserNavigationItem {
    return {
      newWindow: true,
      hideNewWindowIcon: true,
      hideSession: true,
      applicationKey,
      absoluteUrl,
      iconKey,
      label,
    };
  }
}
